import { useEffect, useState } from "react";
import { readJson, writeJson } from "../lib/storage";

/**
 * How large the till is drawn on this device (ADR 0021; Furkan, 04/10/2026: "gorunum 100 ve 90% arasinda
 * ayarlanabilir olsun", "birine S birine M diyelim?"): "M" as the screens were drawn, "S" at 90%, letters
 * and all, as the zoom of a browser does. The style sheet draws it (base.css) from what is put on the
 * page here.
 */
export type Size = "s" | "m";

/** In per cent of the size the screens were drawn in. */
export const SIZE_PERCENT: Record<Size, number> = { s: 90, m: 100 };

/** A device that never chose: Furkan found the till better at 90%. */
const DEFAULT_SIZE: Size = "s";
const KEY = "tafo.size";

function storedSize(): Size {
  const size = readJson<unknown>(KEY, DEFAULT_SIZE);
  return size === "s" || size === "m" ? size : DEFAULT_SIZE;
}

function put(size: Size): void {
  const page = document.documentElement;
  page.dataset.size = size;
  page.style.setProperty("--zoom", String(SIZE_PERCENT[size] / 100));
}

/**
 * The size this device remembers, put on the page before anything is drawn: the opening and the screens
 * before the till (starting, pairing, the terms) have it too.
 */
export function applyStoredSize(): void {
  put(storedSize());
}

/**
 * The Tafo app is always drawn at its own size: the till's "S" stays on the page when the owner's phone goes
 * from Kassa back to Ober or Koerier without loading the page again (Furkan, 19/10/2026: "Ober ⌄" and
 * "Koerier ⌄" were each time another size). The till puts its size back when it opens (App).
 */
export function clearSize(): void {
  const page = document.documentElement;
  delete page.dataset.size;
  page.style.removeProperty("--zoom");
}

/** "S" or "M", remembered per device. */
export function useSize(): [Size, (size: Size) => void] {
  const [size, setSize] = useState<Size>(storedSize);
  useEffect(() => {
    put(size);
    writeJson(KEY, size);
  }, [size]);
  return [size, setSize];
}
